import React, { useEffect, useState, useRef } from 'react';
import { getTenant } from '../../../theme/tokens';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { SearchIcon, CreditCardIcon, AlertCircleIcon, UserIcon, MailIcon, PhoneIcon, DollarSignIcon, CheckIcon, XIcon, ChevronRightIcon, ShieldCheckIcon } from 'lucide-react';
import { MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CommonPaymentOptions } from './CommonPaymentOptions';
interface CardOnFilePaymentProps {
  onExpiryDateChange?: (date: string) => void;
  onAmountChange?: (amount: string) => void;
  onCurrencyChange?: (currency: string) => void;
  onReferenceChange?: (reference: string) => void;
  onSuccess?: () => void;
}
interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  metadata: {
    ltv: string;
    since: string;
    status: string;
  };
  cards: Card[];
}
interface Card {
  id: string;
  brand: string;
  last4: string;
  expiry: string;
  isExpired: boolean;
}
const mockCustomers: Customer[] = [{
  id: 'cus_1',
  name: 'John Smith',
  email: 'john@example.com',
  phone: '+1 (555) 123-4567',
  metadata: {
    ltv: `${getTenant().currency.code} 45,000`,
    since: 'Jan 2023',
    status: 'Premium'
  },
  cards: [{
    id: 'card_1',
    brand: 'Visa',
    last4: '4242',
    expiry: '12/27',
    isExpired: false
  }, {
    id: 'card_2',
    brand: 'Mastercard',
    last4: '5555',
    expiry: '01/22',
    isExpired: true
  }, {
    id: 'card_3',
    brand: 'Amex',
    last4: '9012',
    expiry: '06/28',
    isExpired: false
  }, {
    id: 'card_4',
    brand: 'Visa',
    last4: '1881',
    expiry: '03/24',
    isExpired: true
  }]
}, {
  id: 'cus_2',
  name: 'Sarah Johnson',
  email: 'sarah@example.com',
  phone: '+1 (555) 987-6543',
  metadata: {
    ltv: `${getTenant().currency.code} 12,500`,
    since: 'Mar 2024',
    status: 'Standard'
  },
  cards: [{
    id: 'card_5',
    brand: 'Mastercard',
    last4: '3000',
    expiry: '09/26',
    isExpired: false
  }, {
    id: 'card_6',
    brand: 'Visa',
    last4: '7744',
    expiry: '11/23',
    isExpired: true
  }, {
    id: 'card_7',
    brand: 'Amex',
    last4: '2200',
    expiry: '04/28',
    isExpired: false
  }]
}, {
  id: 'cus_3',
  name: 'Michael Brown',
  email: 'michael@example.com',
  phone: '+1 (555) 456-7890',
  metadata: {
    ltv: `${getTenant().currency.code} 8,200`,
    since: 'Oct 2024',
    status: 'New'
  },
  cards: [{
    id: 'card_8',
    brand: 'Visa',
    last4: '6633',
    expiry: '02/27',
    isExpired: false
  }, {
    id: 'card_9',
    brand: 'Mastercard',
    last4: '4400',
    expiry: '08/23',
    isExpired: true
  }, {
    id: 'card_10',
    brand: 'Visa',
    last4: '1199',
    expiry: '05/24',
    isExpired: true
  }]
}];
export function CardOnFilePayment({
  onExpiryDateChange,
  onAmountChange,
  onCurrencyChange,
  onReferenceChange,
  onSuccess
}: CardOnFilePaymentProps) {
  const {
    t
  } = useTranslation('payments');
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState(getTenant().currency.code);
  const [reference, setReference] = useState('');
  const [description, setDescription] = useState('');
  const [metadata, setMetadata] = useState<MetadataItem[]>([]);
  const [redirectUrl, setRedirectUrl] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleAmountChange = (v: string) => {
    setAmount(v);
    onAmountChange?.(v);
  };
  const handleCurrencyChange = (v: string) => {
    setCurrency(v);
    onCurrencyChange?.(v);
  };
  const handleReferenceChange = (v: string) => {
    setReference(v);
    onReferenceChange?.(v);
  };
  const handleExpiryDateChange = (v: string) => {
    setExpiryDate(v);
    onExpiryDateChange?.(v);
  };
  const filteredCustomers = searchTerm.trim() ? mockCustomers.filter(c => c.name.toLowerCase().includes(searchTerm.toLowerCase()) || c.email.toLowerCase().includes(searchTerm.toLowerCase()) || c.phone.includes(searchTerm)) : mockCustomers;
  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSearchTerm('');
    setIsDropdownOpen(false);
    const validCards = customer.cards.filter(c => !c.isExpired);
    setSelectedCardId(validCards.length > 0 ? validCards[0].id : null);
  };
  const handleClearCustomer = () => {
    setSelectedCustomer(null);
    setSelectedCardId(null);
    setSearchTerm('');
  };
  const handleConfirmCharge = () => {
    setShowPreviewModal(false);
    onSuccess?.();
  };
  const inputClass = 'block w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors placeholder:text-gray-400';
  const validCards = selectedCustomer?.cards.filter(c => !c.isExpired) || [];
  const selectedCard = selectedCustomer?.cards.find(c => c.id === selectedCardId);
  return <div className="space-y-5" data-id="element-7291">
      {/* ─── CUSTOMER SELECTION ─── */}
      <div className="bg-white rounded-xl border border-gray-200" data-id="element-7292">
        <div className="px-6 py-4 border-b border-gray-200" data-id="element-7293">
          <h2 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7294">
            <UserIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7295" />
            {t('components.BulkPaymentPreview.customer')}
          </h2>
        </div>

        {!selectedCustomer ? <div className="p-6" ref={dropdownRef} data-id="element-7296">
            <div className="relative" data-id="element-7297">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7298">
                <SearchIcon size={16} className="text-gray-400" data-id="element-7299" />
              </div>
              <input type="text" className={`${inputClass} pl-10`} placeholder={t('components.CardOnFilePayment.attrs.search_by_name_email_or_phone')} value={searchTerm} onChange={e => {
            setSearchTerm(e.target.value);
            setIsDropdownOpen(true);
          }} onFocus={() => setIsDropdownOpen(true)} data-id="element-7300" />

              {isDropdownOpen && <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto" data-id="element-7301">
                  {filteredCustomers.length > 0 ? <div data-id="element-7302">
                      {filteredCustomers.map((customer, idx) => <div key={customer.id} className={`px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors ${idx < filteredCustomers.length - 1 ? 'border-b border-gray-100' : ''}`} onMouseDown={e => {
                e.preventDefault();
                handleSelectCustomer(customer);
              }} data-id="element-7303">
                          <div className="flex justify-between items-center" data-id="element-7304">
                            <div data-id="element-7305">
                              <p className="text-sm font-medium text-gray-900" data-id="element-7306">
                                {customer.name}
                              </p>
                              <p className="text-xs text-gray-500 mt-0.5" data-id="element-7307">
                                {customer.email}
                              </p>
                            </div>
                            <span className="text-xs text-gray-400" data-id="element-7308">
                              {customer.cards.filter(c => !c.isExpired).length}{' '}
                              {t('components.CardOnFilePayment.cards')}
                            </span>
                          </div>
                        </div>)}
                    </div> : <div className="px-4 py-6 text-center text-sm text-gray-500" data-id="element-7309">
                      {t('components.CardOnFilePayment.no_customers_found')}
                    </div>}
                </div>}
            </div>
          </div> : <div className="px-6 py-4 flex items-center justify-between" data-id="element-7310">
            <div className="flex items-center min-w-0" data-id="element-7311">
              <div className="h-9 w-9 rounded-full bg-[#E2231A]/10 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-7312">
                <UserIcon size={16} className="text-[#9F140F]" data-id="element-7313" />
              </div>
              <div className="min-w-0" data-id="element-7314">
                <div className="flex items-center gap-2" data-id="element-7315">
                  <p className="text-sm font-semibold text-gray-900 truncate" data-id="element-7316">
                    {selectedCustomer.name}
                  </p>
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-[#E2231A]/10 text-[#E2231A] flex-shrink-0" data-id="element-7317">
                    {selectedCustomer.metadata.status}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500" data-id="element-7318">
                  <span className="flex items-center" data-id="element-7319">
                    <MailIcon size={12} className="mr-1 text-gray-400" data-id="element-7320" />
                    {selectedCustomer.email}
                  </span>
                  <span className="flex items-center" data-id="element-7321">
                    <PhoneIcon size={12} className="mr-1 text-gray-400" data-id="element-7322" />
                    {selectedCustomer.phone}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={handleClearCustomer} className="text-xs font-medium text-[#9F140F] hover:text-[#9F140F]/75 transition-colors flex-shrink-0 ml-4" data-id="element-7323">
              {t('components.CardOnFilePayment.change')}
            </button>
          </div>}
      </div>

      {/* ─── PAYMENT METHOD ─── */}
      {selectedCustomer && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7324">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7325">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7326">
              <CreditCardIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7327" />
              {t('components.CardOnFilePayment.payment_method')}
            </h3>
          </div>

          {selectedCustomer.cards.length > 0 ? <div data-id="element-7328">
              {selectedCustomer.cards.map((card, idx) => <div key={card.id} className={`flex items-center px-6 py-4 transition-colors ${card.isExpired ? 'opacity-50 cursor-not-allowed bg-gray-50/50' : selectedCardId === card.id ? 'bg-[#E2231A]/5 cursor-pointer' : 'hover:bg-gray-50 cursor-pointer'} ${idx < selectedCustomer.cards.length - 1 ? 'border-b border-gray-200' : ''}`} onClick={() => {
          if (!card.isExpired) setSelectedCardId(card.id);
        }} data-id="element-7329">
                  <input type="radio" name="savedCard" className="h-4 w-4 text-[#E2231A] focus:ring-[#E2231A]/20 border-gray-300 flex-shrink-0" checked={selectedCardId === card.id} disabled={card.isExpired} onChange={() => {
            if (!card.isExpired) setSelectedCardId(card.id);
          }} data-id="element-7330" />

                  <div className="ml-4 flex-1 flex items-center justify-between" data-id="element-7331">
                    <div className="flex items-center" data-id="element-7332">
                      <div className={`h-8 w-12 border rounded flex items-center justify-center mr-3 overflow-hidden ${card.isExpired ? 'bg-gray-100 border-gray-200' : 'bg-white border-gray-200'}`} data-id="element-7333">
                        {card.brand.toLowerCase() === 'visa' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/visa_logo.png" alt={t('components.CardOnFilePayment.attrs.visa')} className="h-4 object-contain" data-id="element-7334" /> : card.brand.toLowerCase() === 'mastercard' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/mastercard_logo.png" alt={t('components.CardOnFilePayment.attrs.mastercard')} className="h-6 object-contain" data-id="element-7335" /> : card.brand.toLowerCase() === 'amex' ? <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/akmkk18tu8vmgxet3nfyip/2db2784c-a6fd-4102-a1f3-1a3d11f02729/amex_logo.svg" alt={t('components.CardOnFilePayment.attrs.amex')} className="h-6 object-contain" data-id="element-7336" /> : <span className={`text-[10px] font-bold uppercase ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7337">
                            {card.brand}
                          </span>}
                      </div>
                      <div data-id="element-7338">
                        <p className={`text-sm font-medium ${card.isExpired ? 'text-gray-400' : 'text-gray-900'}`} data-id="element-7339">
                          {card.brand} •••• {card.last4}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5" data-id="element-7340">
                          <p className={`text-xs ${card.isExpired ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-7341">
                            {t('components.CardOnFilePayment.expires')}{' '}
                            {card.expiry}
                          </p>
                          {card.isExpired && <span className="text-[10px] font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded" data-id="element-7342">
                              {t('components.CardOnFilePayment.expired')}
                            </span>}
                        </div>
                      </div>
                    </div>
                    {!card.isExpired && selectedCardId === card.id && <CheckIcon size={16} className="text-[#9F140F] flex-shrink-0" data-id="element-7343" />}
                  </div>
                </div>)}
            </div> : <div className="px-6 py-10 text-center" data-id="element-7344">
              <AlertCircleIcon size={20} className="mx-auto text-gray-400 mb-2" data-id="element-7345" />

              <p className="text-sm font-medium text-gray-700" data-id="element-7346">
                {t('components.CardOnFilePayment.no_cards_on_file')}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-7347">
                {t('components.CardOnFilePayment.this_customer_has_no_saved_cards')}
              </p>
            </div>}
        </div>}

      {/* ─── CHARGE DETAILS ─── */}
      {selectedCustomer && selectedCardId && <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-7348">
          <div className="px-6 py-4 border-b border-gray-200" data-id="element-7349">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center" data-id="element-7350">
              <DollarSignIcon size={16} className="mr-2 text-[#9F140F]" data-id="element-7351" />
              {t('components.CardOnFilePayment.charge_details')}
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-7352">
            <div data-id="element-7353">
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7354">
                {t('components.CardOnFilePayment.payment_amount')}{' '}
                <span className="text-red-400" data-id="element-7355">*</span>
              </label>
              <div className="relative" data-id="element-7356">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7357">
                  <span className="text-sm font-medium text-gray-500" data-id="element-7358">
                    {t('components.BulkPaymentListSimplified.usd')}
                  </span>
                </div>
                <input type="text" className={`${inputClass} pl-12`} placeholder="0.00" value={amount} onChange={e => handleAmountChange(e.target.value)} data-id="element-7359" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-7360">
              <div data-id="element-7361">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7362">
                  {t('components.BulkPaymentPreview.reference')}
                </label>
                <input type="text" className={inputClass} placeholder={t('components.CardOnFilePayment.attrs.eg_invoice_1234')} value={reference} onChange={e => handleReferenceChange(e.target.value)} data-id="element-7363" />
              </div>
              <div data-id="element-7364">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-7365">
                  {t('components.BulkPaymentPreview.description')}
                </label>
                <input type="text" className={inputClass} placeholder={t('components.CardOnFilePayment.attrs.eg_monthly_subscription')} value={description} onChange={e => setDescription(e.target.value)} data-id="element-7366" />
              </div>
            </div>
          </div>
        </div>}

      {/* ─── ADDITIONAL OPTIONS ─── */}
      {selectedCustomer && selectedCardId && <CommonPaymentOptions metadata={metadata} redirectUrl={redirectUrl} expiryDate={expiryDate} onMetadataChange={setMetadata} onRedirectUrlChange={setRedirectUrl} onExpiryDateChange={handleExpiryDateChange} hideExpiry={true} hideRedirect={true} data-id="element-7367" />}

      {/* ─── PREVIEW CHARGE BUTTON ─── */}
      {selectedCustomer && selectedCardId && <div className="flex justify-end" data-id="element-7368">
          <button onClick={() => {
        if (selectedCustomer && selectedCardId && amount) {
          setShowPreviewModal(true);
        }
      }} disabled={!amount} className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#C41B14] disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-7369">
            {t('components.CardOnFilePayment.preview_charge')}
            <ChevronRightIcon size={16} className="ml-1.5" data-id="element-7370" />
          </button>
        </div>}

      {/* ─── PREVIEW MODAL ─── */}
      {showPreviewModal && selectedCustomer && selectedCard && createPortal(<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{
      margin: 0,
      background: 'rgba(17,24,39,0.5)'
    }} data-id="element-7371">
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden" data-id="element-7372">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-7373">
                <h3 className="text-base font-semibold text-gray-900 flex items-center" data-id="element-7374">
                  <ShieldCheckIcon size={18} className="mr-2 text-[#9F140F]" data-id="element-7375" />
                  {t('components.CardOnFilePayment.confirm_charge')}
                </h3>
                <button onClick={() => setShowPreviewModal(false)} aria-label="Close" className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-7376">
                  <XIcon size={18} aria-hidden="true" data-id="element-7377" />
                </button>
              </div>

              <div className="p-6 space-y-5" data-id="element-7378">
                <div className="text-center pb-2" data-id="element-7379">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wider mb-1" data-id="element-7380">
                    {t('components.BulkPaymentPreview.amount')}
                  </p>
                  <p className="text-3xl font-bold text-gray-900" data-id="element-7381">
                    {currency} {amount}
                  </p>
                </div>

                <div className="divide-y divide-gray-200 border border-gray-200 rounded-lg overflow-hidden" data-id="element-7382">
                  <div className="flex justify-between items-start px-4 py-3 bg-gray-50/50" data-id="element-7383">
                    <span className="text-sm text-gray-500" data-id="element-7384">
                      {t('components.BulkPaymentPreview.customer')}
                    </span>
                    <div className="text-right" data-id="element-7385">
                      <p className="text-sm font-medium text-gray-900" data-id="element-7386">
                        {selectedCustomer.name}
                      </p>
                      <p className="text-xs text-gray-500" data-id="element-7387">
                        {selectedCustomer.email}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7388">
                    <span className="text-sm text-gray-500" data-id="element-7389">
                      {t('components.CardOnFilePayment.card')}
                    </span>
                    <span className="text-sm font-medium text-gray-900" data-id="element-7390">
                      {selectedCard.brand} •••• {selectedCard.last4}
                    </span>
                  </div>
                  {reference && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7391">
                      <span className="text-sm text-gray-500" data-id="element-7392">
                        {t('components.BulkPaymentPreview.reference')}
                      </span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-7393">
                        {reference}
                      </span>
                    </div>}
                  {description && <div className="flex justify-between items-center px-4 py-3 bg-gray-50/50" data-id="element-7394">
                      <span className="text-sm text-gray-500" data-id="element-7395">
                        {t('components.BulkPaymentPreview.description')}
                      </span>
                      <span className="text-sm font-medium text-gray-900" data-id="element-7396">
                        {description}
                      </span>
                    </div>}
                </div>

                <div className="bg-[#FEE7E5] border border-[#FBD4D1] rounded-lg p-3 flex items-start" data-id="element-7397">
                  <AlertCircleIcon size={16} className="text-[#F1786F] mr-2 mt-0.5 flex-shrink-0" data-id="element-7398" />

                  <p className="text-xs text-[#EB4A42]" data-id="element-7399">
                    {t('components.CardOnFilePayment.this_will_immediately_charge_the_customers')}
                  </p>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3" data-id="element-7400">
                <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-7401">
                  {t('components.CardOnFilePayment.cancel')}
                </button>
                <button onClick={handleConfirmCharge} className="px-5 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm flex items-center" data-id="element-7402">
                  <CheckIcon size={16} className="mr-1.5" data-id="element-7403" />
                  {t('components.CardOnFilePayment.confirm_charge_2')}
                </button>
              </div>
            </div>
          </div>, document.body)}
    </div>;
}